import { useEffect, useState, type CSSProperties, type FormEvent, type MouseEvent, type ReactNode } from 'react'
import { moveFromDepository, moveToDepository } from '../api/devices'
import {
  addDevice,
  loadDeviceTypeForm,
  loadDeviceTypes,
  loadMultipleQrForm,
  csvImport,
  csvPreview,
  csvTemplate,
  postDownload,
  type CsvPlan,
  type DeviceTypeForm,
} from '../api/inventoree'
import { ApiError } from '../api/client'
import type { Named } from '../api/shell'
import { toast } from '../ui/notify'

/**
 * Butoanele de pe listele Inventoree care inca deschideau CI3: ADD Device,
 * Import CSV, Blank QR, Export QR. Markupul e al machetelor vechi; POST-ul
 * merge la capetele v4 (writes + export).
 */

const SIMPLE_FORMATS: { value: string; label: string }[] = [
  { value: '3', label: '13*5' },
  { value: '7', label: '13*5 (PVC)' },
  { value: '1', label: '10*4' },
  { value: '2', label: '19*7' },
]
const DETAILED_FORMATS: { value: string; label: string }[] = [
  { value: '4', label: '4*10' },
  { value: '5', label: '3*9' },
  { value: '6', label: '4*14' },
]

function rightsToast(err: unknown) {
  const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'An error occured! Please try again..'
  toast(message, 'Error', 'error')
}

function BootstrapModal({
  id,
  open,
  onClose,
  title,
  labelledBy = 'myModalLabel',
  footer,
  bodyClass,
  bodyStyle,
  dialogClass,
  children,
}: {
  id: string
  open: boolean
  onClose(): void
  title: ReactNode
  labelledBy?: string
  footer: ReactNode
  bodyClass?: string
  bodyStyle?: CSSProperties
  /** `modal-lg` pentru ferestrele cu tabel (previzualizarea importului). */
  dialogClass?: string
  children: ReactNode
}) {
  const [phase, setPhase] = useState<'hidden' | 'in' | 'leaving'>(open ? 'in' : 'hidden')
  const shown = open || phase !== 'hidden'
  const inClass = open && phase === 'in'

  useEffect(() => {
    if (open) {
      const frame = requestAnimationFrame(() => setPhase('in'))
      return () => cancelAnimationFrame(frame)
    }
    const frame = requestAnimationFrame(() => setPhase('leaving'))
    const timer = setTimeout(() => setPhase('hidden'), 300)
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [open])

  useEffect(() => {
    if (!shown) return
    document.body.classList.add('modal-open')
    const backdrop = document.createElement('div')
    backdrop.className = 'modal-backdrop fade'
    document.body.appendChild(backdrop)
    const frame = requestAnimationFrame(() => backdrop.classList.add('in'))
    return () => {
      cancelAnimationFrame(frame)
      backdrop.classList.remove('in')
      setTimeout(() => backdrop.remove(), 150)
      document.body.classList.remove('modal-open')
    }
  }, [shown])

  useEffect(() => {
    if (!open) return
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <div
      className={`modal fade${inClass ? ' in' : ''}`}
      id={id}
      tabIndex={-1}
      role="dialog"
      aria-labelledby={labelledBy}
      aria-hidden={shown ? undefined : 'true'}
      style={shown ? { display: 'block' } : undefined}
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose()
      }}
    >
      <div className={dialogClass ? `modal-dialog ${dialogClass}` : 'modal-dialog'} role="document">
        <div className="modal-content">
          <div className="modal-header">
            <button type="button" className="close" aria-label="Close" onClick={onClose}>
              <span aria-hidden="true">&times;</span>
            </button>
            <h4 className="modal-title" id={labelledBy}>
              {title}
            </h4>
          </div>
          <div className={bodyClass ?? 'modal-body'} style={bodyStyle}>
            {children}
          </div>
          <div className="modal-footer">{footer}</div>
        </div>
      </div>
    </div>
  )
}

function TypeFields({ page }: { page: DeviceTypeForm }) {
  return (
    <>
      {page.subTypes.length > 0 && (
        <div className="form-group">
          <label className="col-sm-4 control-label">
            Sub-Type <span className="required">*</span>
          </label>
          <div className="col-sm-8">
            <select name="device_subtype" className="form-control" defaultValue="">
              <option value="">Select...</option>
              {page.subTypes.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      <div className="form-group">
        <label className="col-sm-4 control-label">
          Name <span className="required">*</span>
        </label>
        <div className="col-sm-8">
          <input type="text" className="form-control" name="device_name" required />
        </div>
      </div>
      <div className="form-group">
        <label className="col-sm-4 control-label">
          Office <span className="required">*</span>
        </label>
        <div className="col-sm-8 selectContainer">
          <select name="device_office" className="form-control" required defaultValue="">
            <option value="">Select...</option>
            {page.offices.map((o) => (
              <option key={o.id} value={String(o.id)}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label className="col-sm-4 control-label">Group</label>
        <div className="col-sm-8">
          <select name="device_group" className="form-control" defaultValue="">
            <option value="">Select...</option>
            {page.groups.map((g) => (
              <option key={g.id} value={String(g.id)}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label className="col-sm-4 control-label">Virtual machine</label>
        <div className="col-sm-8">
          <input type="checkbox" className="vm_switchery" name="virtual_machine" value="1" />
        </div>
      </div>
      <div className="form-group">
        <label className="col-sm-4 control-label">Brand</label>
        <div className="col-sm-8">
          <select name="device_brand" className="form-control" defaultValue="">
            <option value="">Select...</option>
            {page.brands.map((b) => (
              <option key={b.id} value={String(b.id)}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label className="col-sm-4 control-label">
          Model <span className="required">*</span>
        </label>
        <div className="col-sm-8">
          <input type="text" className="form-control" name="device_model" required />
        </div>
      </div>
      <div className="form-group">
        <label className="col-sm-4 control-label">Serial</label>
        <div className="col-sm-8">
          <input type="text" className="form-control" name="device_serial" />
        </div>
      </div>
      <div className="form-group">
        <label className="col-sm-4 control-label">User</label>
        <div className="col-sm-8">
          <select name="device_owner" className="form-control owner_list_autocomplete" defaultValue="">
            <option value="">Select...</option>
            {page.owners.map((o) => (
              <option key={o.id} value={String(o.id)}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label className="col-sm-4 control-label">Classified info</label>
        <div className="col-sm-8">
          <textarea className="form-control" name="device_classified_info"></textarea>
        </div>
      </div>
      {page.customFields.map((field) => (
        <div className="form-group" key={field.id}>
          <label className="col-sm-4 control-label">{field.label}</label>
          <div className="col-sm-8">
            {field.field_type === 2 ? (
              <select className="form-control" name={`custom_field_${field.id}`} defaultValue="">
                <option value="">Select...</option>
                {field.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : field.field_type === 3 ? (
              <input type="number" step="any" className="form-control" name={`custom_field_${field.id}`} />
            ) : field.field_type === 4 ? (
              <input type="date" className="form-control" name={`custom_field_${field.id}`} />
            ) : field.field_type === 5 ? (
              <select className="form-control" name={`custom_field_${field.id}`} defaultValue="">
                <option value="">Select...</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            ) : field.field_type === 6 ? (
              <textarea rows={4} className="form-control" name={`custom_field_${field.id}`}></textarea>
            ) : (
              <input type="text" className="form-control" name={`custom_field_${field.id}`} />
            )}
          </div>
        </div>
      ))}
    </>
  )
}

export function NewDeviceModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const [types, setTypes] = useState<Named[]>([])
  const [type, setType] = useState('')
  const [page, setPage] = useState<DeviceTypeForm | null>(null)
  /* Lista a venit (chiar goala)? Goala si venita = furnizorul n-are niciun
     tip pornit; serverul nu mai cade pe tot catalogul. */
  const [typesLoaded, setTypesLoaded] = useState(false)

  useEffect(() => {
    if (!open) {
      setType('')
      setPage(null)
      setTypesLoaded(false)
      return
    }
    void loadDeviceTypes()
      .then((list) => {
        setTypes(list)
        setTypesLoaded(true)
      })
      .catch((err: unknown) => rightsToast(err))
  }, [open])

  const onType = (value: string) => {
    setType(value)
    if (Number(value) > 0) {
      void loadDeviceTypeForm(Number(value))
        .then(setPage)
        .catch((err: unknown) => {
          setPage(null)
          rightsToast(err)
        })
    } else {
      setPage(null)
    }
  }

  const save = async () => {
    const form = document.getElementById('newDevice') as HTMLFormElement | null
    if (!form) return
    const fields: Record<string, string> = {}
    const data = new FormData(form)
    data.forEach((value, key) => {
      if (typeof value === 'string') fields[key] = value
    })
    try {
      await addDevice(fields)
      onClose()
      toast('New device was added', 'Success', 'success')
      onSaved()
    } catch (err) {
      rightsToast(err)
    }
  }

  return (
    <BootstrapModal
      id="newDeviceModal"
      open={open}
      onClose={onClose}
      title="New device"
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn btn-primary saveDevice" onClick={() => void save()}>
            Save new device
          </button>
        </>
      }
    >
      <div className="panel-body">
        <form action="/api/v2/inventoree/devices" method="post" id="newDevice" className="form-horizontal fv-form fv-form-bootstrap">
          <div className="form-group">
            <label className="col-sm-4 control-label">
              Device Type <span className="required">*</span>
            </label>
            <div className="col-sm-8 selectContainer">
              <select name="device_type" className="form-control deviceType" value={type} onChange={(e) => onType(e.target.value)}>
                <option value="">Select...</option>
                {types.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {typesLoaded && types.length === 0 && (
            <div className="alert alert-warning" id="noDeviceTypes">
              <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> No device type is turned on for your organization, so a
              device cannot be added yet. Turn on the types you use in{' '}
              <a href="/app/settings/inventoree/device_types">Settings &gt; Inventoree &gt; Device types</a>.
            </div>
          )}
          <div className="templateContainer">
            {page ? (
              <TypeFields page={page} />
            ) : (
              <div className="alert alert-info">
                <i className="fas fa-info-circle"></i> Select device type first
              </div>
            )}
          </div>
        </form>
      </div>
    </BootstrapModal>
  )
}

const CSV_ACTION: Record<string, string> = {
  create: 'Create',
  update: 'Update',
  error: 'Refused',
}

/**
 * Inventoree > Agentless > Import CSV - `Inventoree::uploadCsv`, in doi pasi.
 *
 * Alegerea fisierului porneste verificarea (nu scrie nimic): fiecare rand
 * spune daca CREEAZA un aparat, ACTUALIZEAZA unul agentless cu acelasi Unique,
 * sau e REFUZAT, si de ce. Butonul Import scrie - totul sau nimic, ca in
 * portal - si e oprit cat timp exista un rand refuzat.
 *
 * Erorile se scriu ca text. In portal veneau cu `<b>` si se lipeau ca HTML,
 * cu numele tipului din fisier inauntru.
 */
export function CsvModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const [file, setFile] = useState<File | null>(null)
  const [plan, setPlan] = useState<CsvPlan | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reset = () => {
    setFile(null)
    setPlan(null)
    setProblem(null)
    setBusy(false)
    const input = document.querySelector<HTMLInputElement>('#uploadCsv input[name="csv"]')
    if (input) input.value = ''
  }

  const close = () => {
    reset()
    onClose()
  }

  const choose = (picked: File | null) => {
    setFile(picked)
    setPlan(null)
    setProblem(null)
    if (picked === null) return
    setBusy(true)
    csvPreview(picked)
      .then((result) => {
        setBusy(false)
        if (result.status !== 'OK' || !result.rows) {
          setProblem(result.message || 'That file could not be read.')
          return
        }
        setPlan(result as CsvPlan)
      })
      .catch((err: unknown) => {
        setBusy(false)
        rightsToast(err)
      })
  }

  const write = () => {
    if (file === null || plan === null) return
    setBusy(true)
    csvImport(file)
      .then((result) => {
        setBusy(false)
        if (result.status !== 'OK') {
          if (result.plan) setPlan(result.plan)
          toast(result.message || 'Nothing was imported.', 'Error', 'error')
          return
        }
        toast(result.message || 'CSV file has been imported!', 'Success', 'success')
        close()
        onSaved()
      })
      .catch((err: unknown) => {
        setBusy(false)
        rightsToast(err)
      })
  }

  const writable =
    plan !== null && plan.fileErrors.length === 0 && plan.invalid === 0 && !plan.truncated && plan.create + plan.update > 0

  return (
    <BootstrapModal
      id="csvModal"
      open={open}
      onClose={close}
      title="Import CSV"
      dialogClass="modal-lg"
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={close}>
            Close
          </button>
          <button type="button" className="btn btn-primary saveCsv" disabled={!writable || busy} onClick={write}>
            <i className="far fa-save"></i>{' '}
            {plan !== null && writable ? `Import (${plan.create} new, ${plan.update} updated)` : 'Import'}
          </button>
        </>
      }
    >
      <div className="alert alert-info">
        <i className="fas fa-info-circle"></i> One row per device. Columns are matched by their heading, so the order
        does not matter. A row whose <b>Unique</b> belongs to an agentless device of this company updates that device;
        any other row creates one. Nothing is written until you press Import, and nothing at all if a row is refused.
        <div style={{ paddingTop: 10 }}>
          <span className="label label-danger">
            <b>Unique*</b>
          </span>{' '}
          <span className="label label-danger">
            <b>Type*</b>
          </span>{' '}
          <span className="label label-danger">
            <b>Name*</b>
          </span>{' '}
          <span className="label label-danger">
            <b>Office*</b>
          </span>{' '}
          <span className="label label-danger">
            <b>Model*</b>
          </span>{' '}
          <span className="label label-info">
            <b>Group</b>
          </span>{' '}
          <span className="label label-info">
            <b>Brand</b>
          </span>{' '}
          <span className="label label-info">
            <b>Serial</b>
          </span>{' '}
          <span className="label label-info">
            <b>Details</b>
          </span>{' '}
          <button
            type="button"
            className="btn btn-link btn-xs csvTemplate"
            onClick={() => {
              csvTemplate().catch(() => toast('The template could not be downloaded.', 'Error', 'error'))
            }}
          >
            <i className="fa fa-download"></i> Template
          </button>
        </div>
      </div>
      <form className="form-horizontal" id="uploadCsv" onSubmit={(ev) => ev.preventDefault()}>
        <div className="form-group">
          <div className="col-md-6 fieldContainer">
            <label className="form-label" htmlFor="csvFile">
              File (.csv, .txt, .xlsx, .xls, .ods) <span className="required">*</span>
            </label>
            <input
              type="file"
              className="form-control"
              id="csvFile"
              name="csv"
              accept=".csv,.txt,.xlsx,.xls,.ods"
              onChange={(ev) => choose(ev.target.files?.[0] ?? null)}
            />
          </div>
        </div>
      </form>

      <div className="csvResult">
        {busy ? <p className="text-muted">Checking the file…</p> : null}
        {problem !== null ? <div className="alert alert-danger">{problem}</div> : null}
        {plan !== null ? <CsvPlanView plan={plan} /> : null}
      </div>
    </BootstrapModal>
  )
}

function CsvPlanView({ plan }: { plan: CsvPlan }) {
  if (plan.fileErrors.length > 0) {
    return (
      <div className="alert alert-danger csvFileErrors">
        <b>The file cannot be imported:</b>
        <ul style={{ marginBottom: 0 }}>
          {plan.fileErrors.map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      </div>
    )
  }

  const refused = plan.invalid > 0

  return (
    <>
      <p className="csvSummary">
        <b>{plan.create}</b> to create, <b>{plan.update}</b> to update, <b className={refused ? 'text-danger' : undefined}>{plan.invalid}</b>{' '}
        refused.
        {refused ? ' Fix the refused rows in your file and choose it again - nothing is imported while one is refused.' : null}
      </p>
      {plan.truncated ? <div className="alert alert-warning">The file has more rows than one import takes. Split it.</div> : null}
      {plan.newGroups.length > 0 ? <p className="text-muted small">New groups: {plan.newGroups.join(', ')}</p> : null}
      {plan.newBrands.length > 0 ? <p className="text-muted small">New brands: {plan.newBrands.join(', ')}</p> : null}
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        <table className="table table-condensed csvRows">
          <thead>
            <tr>
              <th>Line</th>
              <th>Unique</th>
              <th>Name</th>
              <th>Type</th>
              <th>Office</th>
              <th>What happens</th>
            </tr>
          </thead>
          <tbody>
            {plan.rows.map((row) => (
              <tr key={row.line} className={row.action === 'error' ? 'danger' : undefined} data-action={row.action}>
                <td>{row.line}</td>
                <td>{row.unique}</td>
                <td>{row.name}</td>
                <td>{row.type}</td>
                <td>{row.office}</td>
                <td>
                  {CSV_ACTION[row.action] ?? row.action}
                  {row.errors.map((err) => (
                    <div key={err} className="text-danger small">
                      {err}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

/** Ordinea din blank_qr_code.php, inainte de changeQrOptions. */
const INITIAL_SIMPLE_FORMATS: { value: string; label: string }[] = [
  { value: '1', label: '10*4' },
  { value: '2', label: '19*7' },
  { value: '3', label: '13*5' },
  { value: '7', label: '13*5 (PVC)' },
]

function QrFields({
  countLabel,
  kind,
  formats,
  onKind,
}: {
  countLabel: string
  kind: string
  formats: { value: string; label: string }[]
  onKind(value: string): void
}) {
  return (
    <div className="form-group">
      <div className="row">
        <div className="col-md-4 fieldContainer">
          <label className="form-label">
            {countLabel} <span className="required">*</span>
          </label>
          <input className="form-control" type="text" required defaultValue="" name="nr" />
        </div>
        <div className="col-md-4 fieldContainer selectContainer">
          <label className="form-label">
            Type <span className="required">*</span>
          </label>
          <select className="form-control" required name="type" value={kind} onChange={(e) => onKind(e.target.value)}>
            <option value="1">Simple QR</option>
            <option value="2">Detailed QR</option>
          </select>
        </div>
        <div className="col-md-4 fieldContainer selectContainer">
          <label className="form-label">
            Format <span className="required">*</span>
          </label>
          <select className="form-control" required name="format" key={kind} defaultValue={formats[0]?.value}>
            {formats.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}

export function BlankQrModal({ open, onClose }: { open: boolean; onClose(): void }) {
  const [kind, setKind] = useState('1')
  const [formats, setFormats] = useState(INITIAL_SIMPLE_FORMATS)

  useEffect(() => {
    if (open) {
      setKind('1')
      setFormats(INITIAL_SIMPLE_FORMATS)
    }
  }, [open])

  const onKind = (value: string) => {
    setKind(value)
    setFormats(value === '2' ? DETAILED_FORMATS : SIMPLE_FORMATS)
  }

  const exportPdf = async (ev: FormEvent) => {
    ev.preventDefault()
    const form = ev.currentTarget as HTMLFormElement
    try {
      await postDownload('/inventoree/qr/export-blank', new FormData(form))
      onClose()
    } catch (err) {
      rightsToast(err)
    }
  }

  return (
    <>
      <style type="text/css">{`
        #export_blank_qr .form-control-feedback { right: 15px; }
        #export_blank_qr .selectContainer .form-control-feedback { right: 25px; }
        #export_blank_qr .selectContainer-bh .form-control-feedback { right: 60px; }
      `}</style>
      <BootstrapModal
        id="exportBlankQRModal"
        open={open}
        onClose={onClose}
        title="Export Blank QR"
        labelledBy="gridSystemModalLabel"
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>
            <button type="button" className="btn btn-primary export_blank_qr" onClick={() => (document.getElementById('export_blank_qr') as HTMLFormElement | null)?.requestSubmit()}>
              Export PDF
            </button>
          </>
        }
      >
        <div className="panel-body">
          <form method="post" action="/api/v2/inventoree/qr/export-blank" className="form-horizontal fv-form fv-form-bootstrap" id="export_blank_qr" encType="multipart/form-data" onSubmit={(e) => void exportPdf(e)}>
            <QrFields countLabel="No. of Codes" kind={kind} formats={formats} onKind={onKind} />
          </form>
        </div>
      </BootstrapModal>
    </>
  )
}

/**
 * Export QR de pe UN rand - qr_code/qr_modal.php + QrForms::single.
 *
 * Acelasi formular ca Blank QR, cu doua deosebiri: prima casuta se cheama
 * "Position" (unde sa cada codul pe coala, nu cate coduri), si pleaca si
 * numarul masinii.
 */
export function SingleQrModal({ device, onClose }: { device: number | null; onClose(): void }) {
  const [kind, setKind] = useState('1')
  const [formats, setFormats] = useState(INITIAL_SIMPLE_FORMATS)

  useEffect(() => {
    if (device !== null) {
      setKind('1')
      setFormats(INITIAL_SIMPLE_FORMATS)
    }
  }, [device])

  const onKind = (value: string) => {
    setKind(value)
    setFormats(value === '2' ? DETAILED_FORMATS : SIMPLE_FORMATS)
  }

  const exportPdf = async (ev: FormEvent) => {
    ev.preventDefault()
    const form = ev.currentTarget as HTMLFormElement
    try {
      await postDownload('/inventoree/qr/export-single', new FormData(form))
      onClose()
    } catch (err) {
      rightsToast(err)
    }
  }

  return (
    <>
      <style type="text/css">{`
        #export_single_qr .form-control-feedback { right: 15px; }
        #export_single_qr .selectContainer .form-control-feedback { right: 25px; }
      `}</style>
      <BootstrapModal
        id="exportQRModal"
        open={device !== null}
        onClose={onClose}
        title="Export QR"
        labelledBy="gridSystemModalLabel"
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary export_single_qr"
              onClick={() => (document.getElementById('export_single_qr') as HTMLFormElement | null)?.requestSubmit()}
            >
              Export PDF
            </button>
          </>
        }
      >
        <div className="panel-body">
          <form
            method="post"
            action="/api/v2/inventoree/qr/export-single"
            className="form-horizontal fv-form fv-form-bootstrap"
            id="export_single_qr"
            encType="multipart/form-data"
            onSubmit={(e) => void exportPdf(e)}
          >
            <QrFields countLabel="Position" kind={kind} formats={formats} onKind={onKind} />
            <input type="hidden" name="device" value={device ?? ''} required />
          </form>
        </div>
      </BootstrapModal>
    </>
  )
}

/**
 * Coloana Actions a fiecarui rand: Export QR, "Move to / from decomissioned".
 *
 * Butoanele nu sunt scrise de ecran, ci de capat, in campul `actions` al
 * randului (`DeviceActions::inventoree`) - deci n-au cum sa aiba fiecare un
 * onClick al lui. In portal le prindea `InventoreeDeviceActions` de pe
 * document; aici le prinde un singur ascultator pe tabel, care e acelasi
 * lucru.
 *
 * Pana acum nu le asculta nimeni: `moveToDepository` si `moveFromDepository`
 * stateau scrise in `api/devices.ts` fara sa le cheme nimeni, iar butonul de QR
 * cerea un `#exportQRModal` care in v4 nu exista. Trei actiuni pe fiecare rand
 * din opt liste, si niciuna nu facea nimic.
 */
export function useRowActions(onChanged: () => void) {
  const [qrDevice, setQrDevice] = useState<number | null>(null)

  const onRowActionClick = (e: MouseEvent<HTMLElement>): void => {
    const target = e.target as HTMLElement

    const qr = target.closest('button.qr')
    if (qr instanceof HTMLElement && qr.dataset.device) {
      e.preventDefault()
      setQrDevice(Number(qr.dataset.device))
      return
    }

    const move = target.closest('.moveToDepository, .moveFromDepository')
    if (!(move instanceof HTMLElement)) {
      return
    }
    e.preventDefault()
    const deviceId = Number(move.dataset.id ?? 0)
    if (deviceId < 1) return
    /* Aceeasi intrebare ca in inventoree.js: mutarea scoate masina din lista in
       care esti, si nu se poate desface de pe rand. */
    if (!window.confirm('Are you sure ?')) return
    const back = move.classList.contains('moveFromDepository')
    void (back ? moveFromDepository(deviceId) : moveToDepository(deviceId))
      .then(() => {
        toast(back ? 'Device moved back.' : 'Device moved to decomissioned.', 'Success')
        onChanged()
      })
      .catch((err: unknown) => rightsToast(err))
  }

  return { onRowActionClick, qrDevice, closeQr: () => setQrDevice(null) }
}

/** Widgetul de selectie multipla: Export QR incarca formularul v4.
 *  Settings is owned by ListSettings (weak leftovers): POST /api/v2/inventoree/bulk/*. */
export function SelectedDevicesWidget({
  shown,
  ids,
  onHide,
  settingsHref,
}: {
  shown: boolean
  ids: number[]
  onHide(): void
  settingsHref: string
}) {
  const [tab, setTab] = useState<'none' | 'qr'>('none')
  const [html, setHtml] = useState('')

  useEffect(() => {
    setTab('none')
    setHtml('')
  }, [ids.join(',')])

  const openQr = (ev: MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault()
    setTab('qr')
    void loadMultipleQrForm(ids)
      .then(setHtml)
      .catch((err: unknown) => rightsToast(err))
  }

  const onContentClick = (ev: MouseEvent<HTMLDivElement>) => {
    const btn = (ev.target as HTMLElement).closest('.export_multi_qr')
    if (!(btn instanceof HTMLElement)) return
    ev.preventDefault()
    const form = btn.closest('form')
    if (!form) return
    void postDownload('/inventoree/qr/export-multiple', new FormData(form)).catch((err: unknown) => rightsToast(err))
  }

  return (
    <div className="multiple_check_widget widget border-brown" style={{ display: shown ? undefined : 'none' }}>
      <div className="widget-header bg-brown">
        <div className="widget-icon">
          <i className="fas fa-building"></i>
        </div>
        <h2 className="widget-title">Selected Devices Options</h2>
        <div className="widget-action">
          <button className="btn hide-widget" type="button" onClick={onHide}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
      <div className="widget-content">
        <div style={{ width: '20%', display: 'inline-block', boxSizing: 'border-box' }}>
          <ul className="nav nav-metro-tabs tabs-left">
            <li data-class-nav="nav-content" data-toggle="tab" className={tab === 'qr' ? 'active' : undefined}>
              <a href="/api/v2/inventoree/qr/multiple-form" onClick={openQr}>
                Export QR
              </a>
            </li>
            <li data-class-nav="nav-content" data-toggle="tab">
              <a href={settingsHref} onClick={(e) => e.preventDefault()}>Settings</a>
            </li>
          </ul>
        </div>
        <div
          className="nav-content"
          style={{ display: 'inline-block', width: '79%', boxSizing: 'border-box', verticalAlign: 'top', paddingLeft: 15 }}
          onClick={onContentClick}
          dangerouslySetInnerHTML={tab === 'qr' ? { __html: html } : undefined}
        />
      </div>
    </div>
  )
}

export { SIMPLE_FORMATS, DETAILED_FORMATS }
